import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatErr } from "./api";
import { useEscapeKey } from "./hooks";
import { toast } from "sonner";
import { Plus, X, ShoppingCart, Eye, Lock, Pencil, CheckCircle2, History } from "lucide-react";

const STATUSES = ["pending", "processing", "packed", "shipped", "delivered", "cancelled", "returned"];

const statusColor = (s) => ({
  pending: "text-amber-400",
  processing: "text-sky-400",
  packed: "text-purple-400",
  shipped: "text-emerald-400",
  delivered: "text-gold",
  cancelled: "text-rose-300",
  returned: "text-rose-300",
}[s] || "text-mist");

const isLocked = (order) =>
  !!order && (order.finalized || order.status === "delivered");
const lockLabel = (order) =>
  !order ? "" : order.finalized ? "Finalised" : order.status === "delivered" ? "Delivered" : "";

const emptyOrder = {
  customer_name: "", customer_email: "", phone: "", address: "",
  items: [], total: 0, status: "pending", notes: "",
  courier: "", tracking_number: "", tracking_url: "", remarks: "",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyOrder);
  const [detail, setDetail] = useState(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackForm, setTrackForm] = useState({ courier: "", tracking_number: "", tracking_url: "", remarks: "" });
  const [trackBusy, setTrackBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyOrder);
  const [editBusy, setEditBusy] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // ESC-to-close — innermost first
  useEscapeKey(editOpen, () => setEditOpen(false));
  useEscapeKey(trackOpen, () => setTrackOpen(false));
  useEscapeKey(open, () => setOpen(false));
  useEscapeKey(!!detail && !editOpen && !trackOpen, () => setDetail(null));

  const load = async () => {
    const [o, p] = await Promise.all([
      adminApi.get("/orders", { params: statusFilter ? { status: statusFilter } : {} }),
      adminApi.get("/products"),
    ]);
    setOrders(o.data.orders);
    setProducts(p.data.products);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [o, p] = await Promise.all([
        adminApi.get("/orders", { params: statusFilter ? { status: statusFilter } : {} }),
        adminApi.get("/products"),
      ]);
      if (cancelled) return;
      setOrders(o.data.orders);
      setProducts(p.data.products);
    })();
    return () => { cancelled = true; };
  }, [statusFilter]);

  const pmap = useMemo(() => Object.fromEntries(products.map((p) => [p.sku, p])), [products]);

  const updateStatus = async (oid, status, extras = {}) => {
    try {
      const payload = { status, ...extras };
      await adminApi.patch(`/orders/${oid}/status`, payload);
      toast.success(`Order ${status}`);
      await load();
      if (detail?.id === oid) setDetail({ ...detail, status, ...extras });
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const openShipDialog = (order) => {
    setDetail(order);
    setTrackForm({
      courier: order.courier || "",
      tracking_number: order.tracking_number || "",
      tracking_url: order.tracking_url || "",
      remarks: order.remarks || "",
    });
    setTrackOpen(true);
  };

  const confirmShip = async (e) => {
    e.preventDefault();
    if (!trackForm.tracking_number.trim()) { toast.error("Tracking number is required"); return; }
    setTrackBusy(true);
    try {
      await updateStatus(detail.id, "shipped", {
        courier: trackForm.courier.trim() || undefined,
        tracking_number: trackForm.tracking_number.trim(),
        tracking_url: trackForm.tracking_url.trim() || undefined,
        remarks: trackForm.remarks.trim() || undefined,
      });
      setTrackOpen(false);
    } finally { setTrackBusy(false); }
  };

  // When status dropdown in a row is changed to "shipped", trigger the modal instead of plain update
  const handleStatusChange = (order, newStatus) => {
    if (isLocked(order)) {
      toast.error(`${lockLabel(order)} — status cannot be changed`);
      return;
    }
    if (newStatus === "shipped") {
      openShipDialog(order);
    } else {
      updateStatus(order.id, newStatus);
    }
  };

  const openEditDialog = (order) => {
    if (isLocked(order)) {
      toast.error(`${lockLabel(order)} — order is locked`);
      return;
    }
    setDetail(order);
    setEditForm({
      customer_name: order.customer_name || "",
      customer_email: order.customer_email || "",
      phone: order.phone || "",
      address: order.address || "",
      items: (order.items || []).map((it) => ({ ...it })),
      total: order.total || 0,
      status: order.status || "pending",
      notes: order.notes || "",
      courier: order.courier || "",
      tracking_number: order.tracking_number || "",
      tracking_url: order.tracking_url || "",
      remarks: order.remarks || "",
    });
    setEditOpen(true);
  };

  const editSet = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });
  const editAddItem = (sku) => {
    if (!sku) return;
    const p = pmap[sku];
    if (!p) return;
    const items = [...editForm.items, { sku: p.sku, name: p.name, qty: 1, price: p.selling_price }];
    setEditForm({ ...editForm, items, total: items.reduce((s, i) => s + i.qty * i.price, 0) });
  };
  const editUpdateQty = (idx, qty) => {
    const items = editForm.items.map((it, i) => (i === idx ? { ...it, qty: Number(qty) || 1 } : it));
    setEditForm({ ...editForm, items, total: items.reduce((s, i) => s + i.qty * i.price, 0) });
  };
  const editRemoveItem = (idx) => {
    const items = editForm.items.filter((_, i) => i !== idx);
    setEditForm({ ...editForm, items, total: items.reduce((s, i) => s + i.qty * i.price, 0) });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!detail) return;
    if (editForm.items.length === 0) { toast.error("Order needs at least one item"); return; }
    setEditBusy(true);
    try {
      const payload = {
        customer_name: editForm.customer_name,
        customer_email: editForm.customer_email || undefined,
        phone: editForm.phone || undefined,
        address: editForm.address || undefined,
        items: editForm.items,
        total: editForm.total,
        notes: editForm.notes || undefined,
        remarks: editForm.remarks || undefined,
      };
      const { data } = await adminApi.put(`/orders/${detail.id}`, payload);
      toast.success("Order updated");
      setEditOpen(false);
      setDetail(data);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setEditBusy(false); }
  };

  const finalizeOrder = async (order) => {
    if (!order || order.finalized) return;
    if (!window.confirm("Finalise this order? After finalisation no more edits or status changes will be possible.")) return;
    setFinalizing(true);
    try {
      await adminApi.post(`/orders/${order.id}/finalize`);
      toast.success("Order finalised");
      await load();
      setDetail((d) => (d && d.id === order.id ? { ...d, finalized: true } : d));
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setFinalizing(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addItem = (sku) => {
    if (!sku) return;
    const p = pmap[sku];
    const items = [...form.items, { sku: p.sku, name: p.name, qty: 1, price: p.selling_price }];
    setForm({ ...form, items, total: items.reduce((s, i) => s + i.qty * i.price, 0) });
  };
  const updateQty = (idx, qty) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, qty: Number(qty) || 1 } : it));
    setForm({ ...form, items, total: items.reduce((s, i) => s + i.qty * i.price, 0) });
  };
  const removeItem = (idx) => {
    const items = form.items.filter((_, i) => i !== idx);
    setForm({ ...form, items, total: items.reduce((s, i) => s + i.qty * i.price, 0) });
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await adminApi.post("/orders", payload);
      toast.success("Order created");
      setOpen(false);
      setForm(emptyOrder);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-gold">Sales pipeline</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Orders</h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-ink-900 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="order-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => setOpen(true)} className="btn-primary !py-3" data-testid="new-order-btn">
            <Plus className="w-3.5 h-3.5" /> New order
          </button>
        </div>
      </div>

      <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="orders-table">
        <table className="w-full text-sm">
          <thead className="bg-ink-950">
            <tr className="text-left label-eyebrow text-mist">
              <th className="px-4 py-3">Order #</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Items</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-white/5 text-bone/90 font-light" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.order_number}</td>
                <td className="px-4 py-3">{o.customer_name}<br /><span className="text-mist text-xs">{o.phone || o.customer_email || ""}</span></td>
                <td className="px-4 py-3 text-mist text-xs">{o.items?.length || 0} item{(o.items?.length || 0) !== 1 ? "s" : ""}</td>
                <td className="px-4 py-3 text-gold">₹{Math.round(o.total).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">
                  {isLocked(o) ? (
                    <span className={`inline-flex items-center gap-1 label-eyebrow ${statusColor(o.status)}`} data-testid={`order-status-${o.id}`}>
                      <Lock className="w-3 h-3" /> {o.status}
                    </span>
                  ) : (
                    <select value={o.status} onChange={(e) => handleStatusChange(o, e.target.value)} className={`bg-transparent border-0 outline-none label-eyebrow ${statusColor(o.status)}`} data-testid={`order-status-${o.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                </td>
                <td className="px-4 py-3 text-mist text-xs">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3">
                  <button onClick={() => setDetail(o)} className="text-mist hover:text-gold p-1.5" data-testid={`view-order-${o.id}`}><Eye className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-mist font-light italic">
                <ShoppingCart className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No orders yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div onClick={(e) => e.stopPropagation()} className="bg-ink-900 border border-white/10 w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
            <div className="flex justify-between items-start">
              <div>
                <p className="label-eyebrow text-gold">Order detail</p>
                <h3 className="font-serif text-2xl text-bone mt-1 font-mono tracking-tight">{detail.order_number}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className={`label-eyebrow ${statusColor(detail.status)}`}>{detail.status}</p>
                  {isLocked(detail) && (
                    <span className="inline-flex items-center gap-1 label-eyebrow text-gold border border-gold/40 px-2 py-0.5" data-testid="order-lock-pill">
                      <Lock className="w-3 h-3" /> {lockLabel(detail)}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>

            {/* Modify / Finalise / Logs toolbar */}
            <div className="mt-4 flex flex-wrap gap-2" data-testid="order-actions-toolbar">
              <button
                onClick={() => openEditDialog(detail)}
                disabled={isLocked(detail)}
                className="label-eyebrow px-3 py-2 border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="modify-order-btn"
              >
                <Pencil className="w-3 h-3" /> Modify
              </button>
              <button
                onClick={() => finalizeOrder(detail)}
                disabled={isLocked(detail) || finalizing}
                className="label-eyebrow px-3 py-2 border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="finalize-order-btn"
              >
                <CheckCircle2 className="w-3 h-3" /> {finalizing ? "Finalising…" : "Finalise"}
              </button>
              <button
                onClick={() => setLogsOpen((v) => !v)}
                className="label-eyebrow px-3 py-2 border border-white/10 text-mist hover:border-gold hover:text-gold transition-colors inline-flex items-center gap-1.5"
                data-testid="toggle-order-logs-btn"
              >
                <History className="w-3 h-3" /> {logsOpen ? "Hide" : "View"} log ({(detail.edit_logs || []).length})
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6 text-sm font-light">
              <div>
                <p className="label-eyebrow text-mist mb-1">Customer</p>
                <p className="text-bone">{detail.customer_name}</p>
                {detail.phone && <p className="text-mist text-xs">{detail.phone}</p>}
                {detail.customer_email && <p className="text-mist text-xs">{detail.customer_email}</p>}
              </div>
              <div>
                <p className="label-eyebrow text-mist mb-1">Shipping</p>
                <p className="text-bone whitespace-pre-wrap">{detail.address || "—"}</p>
              </div>
            </div>
            <div className="mt-6">
              <p className="label-eyebrow text-mist mb-3">Items</p>
              <table className="w-full text-sm">
                <tbody>
                  {detail.items?.map((it, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2 text-bone font-light">{it.name} <span className="text-mist text-xs">({it.sku})</span></td>
                      <td className="py-2 text-mist text-xs w-12 text-right">×{it.qty}</td>
                      <td className="py-2 text-gold text-right w-24">₹{Math.round(it.qty * it.price).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between mt-4 pt-3 border-t border-gold/30">
                <span className="label-eyebrow text-gold">Total</span>
                <span className="num-display text-2xl text-bone">₹{Math.round(detail.total).toLocaleString("en-IN")}</span>
              </div>
            </div>
            {detail.notes && <div className="mt-4 text-sm text-mist font-light italic">&ldquo;{detail.notes}&rdquo;</div>}
            <div className="mt-6 flex gap-2 flex-wrap">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(detail, s)}
                  disabled={detail.status === s || isLocked(detail)}
                  className={`label-eyebrow px-3 py-2 border ${detail.status === s ? "border-gold text-gold" : "border-white/10 text-mist hover:border-gold hover:text-gold"} transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}
                  data-testid={`set-status-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>

            {(detail.tracking_number || detail.courier || detail.remarks) && (
              <div className="mt-6 pt-5 border-t border-white/5 space-y-2 text-sm" data-testid="order-tracking-block">
                <p className="label-eyebrow text-gold flex items-center justify-between">
                  <span>Shipment</span>
                  {detail.status === "shipped" && (
                    <button onClick={() => openShipDialog(detail)} className="text-mist hover:text-gold text-[10px]" data-testid="edit-tracking-btn">
                      Edit tracking →
                    </button>
                  )}
                </p>
                {detail.courier && <p className="text-bone font-light">Courier · <span className="text-gold">{detail.courier}</span></p>}
                {detail.tracking_number && (
                  <p className="text-bone font-light">
                    Tracking · <span className="font-mono text-gold">{detail.tracking_number}</span>
                    {detail.tracking_url && (
                      <a href={detail.tracking_url} target="_blank" rel="noreferrer" className="ml-2 label-eyebrow text-mist hover:text-gold text-[10px]">
                        Track →
                      </a>
                    )}
                  </p>
                )}
                {detail.remarks && <p className="text-mist text-xs font-light italic">&ldquo;{detail.remarks}&rdquo;</p>}
              </div>
            )}

            {logsOpen && (
              <div className="mt-6 pt-5 border-t border-white/5" data-testid="order-edit-logs">
                <p className="label-eyebrow text-gold mb-3">Edit history</p>
                {(detail.edit_logs || []).length === 0 ? (
                  <p className="text-mist text-xs font-light italic">No edits yet.</p>
                ) : (
                  <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {[...(detail.edit_logs || [])].reverse().map((log, i) => (
                      <li key={i} className="bg-ink-950 border border-white/5 p-3 text-xs font-light" data-testid={`log-entry-${i}`}>
                        <div className="flex justify-between text-mist">
                          <span className="label-eyebrow text-gold text-[10px]">{log.action}</span>
                          <span className="text-[10px]">{new Date(log.ts).toLocaleString("en-IN")} · {log.by}</span>
                        </div>
                        {log.changes && Object.keys(log.changes).length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {Object.entries(log.changes).map(([field, val]) => (
                              <li key={field} className="text-bone/90 break-all">
                                <span className="text-mist">{field}:</span>{" "}
                                {typeof val === "object" && val !== null && "from" in val ? (
                                  <>
                                    <span className="line-through text-rose-300/70">{JSON.stringify(val.from) ?? "—"}</span>
                                    <span className="text-mist mx-1">→</span>
                                    <span className="text-emerald-400">{JSON.stringify(val.to) ?? "—"}</span>
                                  </>
                                ) : (
                                  <span className="text-emerald-400">{JSON.stringify(val)}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* New order modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-ink-900 border border-white/10 w-full max-w-2xl p-8 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="order-form-dialog">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-2xl text-bone">New order</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input required value={form.customer_name} onChange={set("customer_name")} placeholder="Customer name" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="order-customer-input" />
              <input type="email" value={form.customer_email} onChange={set("customer_email")} placeholder="Email (optional)" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <select value={form.status} onChange={set("status")} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea value={form.address} onChange={set("address")} placeholder="Shipping address" rows={2} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none" />
              <textarea
                value={form.remarks}
                onChange={set("remarks")}
                placeholder="Remarks (e.g. gift wrap, delivery instructions)"
                rows={2}
                className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none"
                data-testid="order-remarks-input"
              />
            </div>

            {form.status === "shipped" && (
              <div className="bg-ink-950 border border-gold/30 p-4 space-y-3" data-testid="order-shipping-block">
                <p className="label-eyebrow text-gold">Shipment details</p>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    value={form.courier}
                    onChange={set("courier")}
                    placeholder="Courier (e.g. Bluedart)"
                    className="bg-ink-900 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                    data-testid="order-courier-input"
                  />
                  <input
                    required={form.status === "shipped"}
                    value={form.tracking_number}
                    onChange={set("tracking_number")}
                    placeholder="Tracking number"
                    className="bg-ink-900 border border-gold/40 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                    data-testid="order-tracking-input"
                  />
                  <input
                    value={form.tracking_url}
                    onChange={set("tracking_url")}
                    placeholder="Tracking URL (optional)"
                    className="col-span-2 bg-ink-900 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                  />
                </div>
              </div>
            )}

            <div>
              <p className="label-eyebrow text-mist mb-2">Items</p>
              <select onChange={(e) => { addItem(e.target.value); e.target.value = ""; }} className="w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="order-add-item">
                <option value="">— Add product —</option>
                {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} · {p.name} (₹{p.selling_price})</option>)}
              </select>
              <div className="mt-3 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 bg-ink-950 border border-white/10 px-3 py-2 text-sm font-light">
                    <span className="text-bone flex-1">{it.name}</span>
                    <input type="number" min="1" value={it.qty} onChange={(e) => updateQty(i, e.target.value)} className="w-16 bg-transparent border-b border-white/10 text-center text-bone outline-none" />
                    <span className="text-gold w-20 text-right">₹{Math.round(it.qty * it.price).toLocaleString("en-IN")}</span>
                    <button type="button" onClick={() => removeItem(i)} className="text-mist hover:text-rose-300"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <p className="text-right num-display text-2xl text-gold mt-3">Total: ₹{Math.round(form.total).toLocaleString("en-IN")}</p>
            </div>

            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary !py-3" data-testid="save-order-btn">{busy ? "Saving…" : "Create order"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Mark-as-shipped tracking modal */}
      {trackOpen && detail && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6">
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={confirmShip}
            className="bg-ink-900 border border-gold/40 w-full max-w-lg p-8 space-y-4"
            data-testid="ship-tracking-dialog"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="label-eyebrow text-gold">Mark as shipped</p>
                <h3 className="font-serif text-2xl text-bone mt-1 font-mono">{detail.order_number}</h3>
              </div>
              <button type="button" onClick={() => setTrackOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-mist text-xs font-light">Tracking number is required before this order can be marked shipped.</p>
            <div className="grid grid-cols-2 gap-3">
              <input
                value={trackForm.courier}
                onChange={(e) => setTrackForm({ ...trackForm, courier: e.target.value })}
                placeholder="Courier (e.g. Bluedart)"
                className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                data-testid="ship-courier-input"
              />
              <input
                required
                autoFocus
                value={trackForm.tracking_number}
                onChange={(e) => setTrackForm({ ...trackForm, tracking_number: e.target.value })}
                placeholder="Tracking number"
                className="bg-ink-950 border border-gold/50 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                data-testid="ship-tracking-input"
              />
              <input
                value={trackForm.tracking_url}
                onChange={(e) => setTrackForm({ ...trackForm, tracking_url: e.target.value })}
                placeholder="Tracking URL (optional)"
                className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
              />
              <textarea
                value={trackForm.remarks}
                onChange={(e) => setTrackForm({ ...trackForm, remarks: e.target.value })}
                placeholder="Remarks (optional)"
                rows={2}
                className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none"
                data-testid="ship-remarks-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setTrackOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={trackBusy} className="btn-primary !py-3" data-testid="confirm-ship-btn">
                {trackBusy ? "Saving…" : "Mark shipped"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modify order modal */}
      {editOpen && detail && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6">
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveEdit}
            className="bg-ink-900 border border-gold/40 w-full max-w-2xl p-8 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="edit-order-dialog"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="label-eyebrow text-gold">Modify order</p>
                <h3 className="font-serif text-2xl text-bone mt-1 font-mono">{detail.order_number}</h3>
              </div>
              <button type="button" onClick={() => setEditOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-mist text-xs font-light">All changes are recorded in the order&apos;s edit log.</p>
            <div className="grid grid-cols-2 gap-3">
              <input required value={editForm.customer_name} onChange={editSet("customer_name")} placeholder="Customer name" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="edit-customer-input" />
              <input type="email" value={editForm.customer_email} onChange={editSet("customer_email")} placeholder="Email" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input value={editForm.phone} onChange={editSet("phone")} placeholder="Phone" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <textarea value={editForm.address} onChange={editSet("address")} placeholder="Shipping address" rows={2} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none" />
              <textarea value={editForm.notes} onChange={editSet("notes")} placeholder="Notes" rows={2} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none" data-testid="edit-notes-input" />
              <textarea value={editForm.remarks} onChange={editSet("remarks")} placeholder="Remarks" rows={2} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none" />
            </div>
            <div>
              <p className="label-eyebrow text-mist mb-2">Items</p>
              <select onChange={(e) => { editAddItem(e.target.value); e.target.value = ""; }} className="w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="edit-add-item">
                <option value="">— Add product —</option>
                {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} · {p.name} (₹{p.selling_price})</option>)}
              </select>
              <div className="mt-3 space-y-2">
                {editForm.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 bg-ink-950 border border-white/10 px-3 py-2 text-sm font-light">
                    <span className="text-bone flex-1">{it.name}</span>
                    <input type="number" min="1" value={it.qty} onChange={(e) => editUpdateQty(i, e.target.value)} className="w-16 bg-transparent border-b border-white/10 text-center text-bone outline-none" />
                    <span className="text-gold w-20 text-right">₹{Math.round(it.qty * it.price).toLocaleString("en-IN")}</span>
                    <button type="button" onClick={() => editRemoveItem(i)} className="text-mist hover:text-rose-300"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <p className="text-right num-display text-2xl text-gold mt-3">Total: ₹{Math.round(editForm.total).toLocaleString("en-IN")}</p>
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setEditOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={editBusy} className="btn-primary !py-3" data-testid="save-edit-order-btn">
                {editBusy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
